/**
 * InviteUserModal Component
 *
 * Form modal for inviting a new user with email and role selection.
 * The actual invitation is delegated via the `onSubmit` callback.
 *
 * Uses `useTranslation('modules.modals.inviteUser')` for labels.
 *
 * @example
 * ```tsx
 * <InviteUserModal
 *   open={showInvite}
 *   roleOptions={[{ value: 'ADMIN', label: 'Admin' }, { value: 'USER', label: 'User' }]}
 *   onSubmit={async ({ email, roles }) => await inviteUser(email, roles)}
 *   onClose={() => setShowInvite(false)}
 * />
 * ```
 */
import * as React from 'react';
import { useState } from 'react';
import { type TestIdProps } from '../../utils';
import { FormModal } from '../../components/FormModal';
import { Input } from '../../components/inputs';
import { InputWrapper } from '../../components/InputWrapper';
import { Select } from '../../components/selects/Select';
import { useTranslation } from '../../providers/UIProvider';

export interface InviteUserFormData {
  email: string;
  roles: string[];
}

export interface InviteUserModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Available role options for the multi-select; a description shows under its option */
  roleOptions: Array<{ value: string; label: string; description?: string }>;
  /** Roles preselected when the form opens */
  defaultRoles?: readonly string[];
  /** Shown under the roles while none is selected — what the person gets without a role */
  noRolesHint?: string;
  /** Called with form data — should return on success or throw on error */
  onSubmit: (data: InviteUserFormData) => Promise<void>;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Whether the submission is in progress */
  loading?: boolean;
  /** External error message */
  error?: string | null;
  /** Optional email validation function */
  validateEmail?: (email: string) => boolean;
  /** Extra fields the caller owns and submits itself, rendered between the e-mail and the roles */
  extraFields?: React.ReactNode;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  roleOptions,
  defaultRoles = [],
  noRolesHint,
  onSubmit,
  onClose,
  loading = false,
  error: externalError,
  validateEmail,
  extraFields
}) => {
  const t = useTranslation('modules.modals.inviteUser');
  const [formData, setFormData] = useState<InviteUserFormData>({
    email: '',
    roles: [...defaultRoles]
  });
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLocalError(null);

    if (!formData.email.trim()) {
      setLocalError(t('errors.emailRequired'));
      return;
    }

    if (validateEmail && !validateEmail(formData.email)) {
      setLocalError(t('errors.emailInvalid'));
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('errors.inviteError');
      setLocalError(errorMsg);
    }
  };

  const handleRolesChange = (value: string | number | (string | number)[]) => {
    const roles = Array.isArray(value) ? value.map(v => String(v)) : [String(value)];
    setFormData({ ...formData, roles });
  };

  const displayError = localError || externalError || null;

  return (
    <FormModal
      open={open}
      title={t('title')}
      description={t('description')}
      submitLabel={loading ? t('status.inviting') : t('title')}
      cancelLabel={t('actions.cancel')}
      loading={loading}
      submitDisabled={!formData.email.trim()}
      error={displayError}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <InputWrapper label={t('fields.email')} htmlFor='email' labelSize='sm' required>
        <Input
          id='email'
          type='email'
          placeholder={t('placeholders.email')}
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
          disabled={loading}
          required
        />
      </InputWrapper>

      {extraFields}

      <InputWrapper
        label={t('fields.role')}
        htmlFor='role'
        labelSize='sm'
        className='min-h-96'
        hint={formData.roles.length === 0 ? noRolesHint : undefined}
      >
        <Select
          id='role'
          isMulti
          value={formData.roles}
          onChange={handleRolesChange}
          options={roleOptions}
          disabled={loading}
        />
      </InputWrapper>
    </FormModal>
  );
};

InviteUserModal.displayName = 'InviteUserModal';
